import React, { Component } from "react";

/**
 * LoginPanel — Animated left-side feature showcase for the login screen.
 *
 * HOW TO USE:
 * In your Login/Register component, replace the image background section with:
 *
 *   import LoginPanel from "./LoginPanel";
 *   ...
 *   // Where you currently have the .login-container with image1:
 *   <LoginPanel />
 *
 * The component is fully self-contained — no props needed, no external deps.
 * It matches your existing brand colors (navy #1A2280, gold #D4921A, green #1A5C40).
 */

const FEATURES = [
  {
    icon: "🤖",
    category: "AI-Powered Tools",
    title: "Research Any Property in Seconds",
    body: "Full public records, verified market value, comparable sales, neighborhood health scores, inspection checklists, and AI analysis — all in one report.",
    stats: [
      { val: "AI", label: "Powered" },
      { val: "100+", label: "Data Points" },
      { val: "Free", label: "To Start" },
    ],
    accent: "#D4921A",
  },
  {
    icon: "🏠",
    category: "Buy a Home",
    title: "Search MLS + Off-Market — All in One Place",
    body: "Find homes you won't see anywhere else. See exactly who the listing agent is. Decide who you want to talk to — and when. Full transparency, your way.",
    stats: [
      { val: "On+Off", label: "MLS Listings" },
      { val: "100%", label: "Transparent" },
      { val: "You", label: "In Control" },
    ],
    accent: "#1A5C40",
  },
  {
    icon: "📋",
    category: "Inspection Analysis",
    title: "Upload Any Inspection Report — AI Flags Every Risk",
    body: "Upload a PDF inspection report and our AI identifies critical issues, estimates repair costs, and gives you a negotiation strategy — before you make an offer.",
    stats: [
      { val: "PDF", label: "Upload" },
      { val: "AI", label: "Risk Analysis" },
      { val: "Cost", label: "Estimates" },
    ],
    accent: "#4A3A6A",
  },
  {
    icon: "🎯",
    category: "Buyer Readiness",
    title: "Know Your Buying Power Before You Start",
    body: "Answer 8 quick questions. Get your readiness score, estimated buying power range, debt-to-income ratio, and a personalized action plan — free, in 2 minutes.",
    stats: [
      { val: "8", label: "Questions" },
      { val: "DTI", label: "Calculated" },
      { val: "Score", label: "Out of 90%" },
    ],
    accent: "#7A2A1A",
  },
  {
    icon: "💰",
    category: "Sell Your Home",
    title: "Sell on Your Terms — No MLS Pressure",
    body: "List for free, off-market or on. Set your own timeline. Search for matching buyer profiles — cash buyers, investors, and more — without the traditional hassle.",
    stats: [
      { val: "Free", label: "To List" },
      { val: "Your", label: "Timeline" },
      { val: "Off", label: "MLS Option" },
    ],
    accent: "#1A5C40",
  },
  {
    icon: "🏡",
    category: "Homeowner Portal",
    title: "Manage Your Home — Healthy, Wealthy & Wise",
    body: "Track your home's health, monitor your equity, get maintenance reminders, and access AI-powered advice. Your home is your biggest investment — manage it like one.",
    stats: [
      { val: "Health", label: "Tracking" },
      { val: "Equity", label: "Monitor" },
      { val: "AI", label: "Advisor" },
    ],
    accent: "#D4921A",
  },
  {
    icon: "📍",
    category: "Prospecting Map",
    title: "Map Every Property — Save, Track & Research",
    body: "Drop pins on any property while you're in the field. Pull public records instantly, save to your prospecting list, and run full AI research — all from the map.",
    stats: [
      { val: "GPS", label: "Enabled" },
      { val: "Public", label: "Records" },
      { val: "Save", label: "& Track" },
    ],
    accent: "#1A2280",
  },
];

const INTERVAL = 4500; // ms per slide

const styles = {
  panel: {
    position: "relative",
    width: "100%",
    height: "100%",
    minHeight: "100vh",
    background: "linear-gradient(160deg, #0d1260 0%, #1A2280 35%, #1a3a28 75%, #0d2418 100%)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    fontFamily: "'DM Sans', sans-serif",
  },

  /* Animated background circles */
  bgCircle1: {
    position: "absolute", top: "-100px", right: "-80px",
    width: "380px", height: "380px", borderRadius: "50%",
    background: "rgba(212,146,26,0.06)", pointerEvents: "none",
    animation: "pulse1 8s ease-in-out infinite",
  },
  bgCircle2: {
    position: "absolute", bottom: "60px", left: "-100px",
    width: "320px", height: "320px", borderRadius: "50%",
    background: "rgba(26,92,64,0.12)", pointerEvents: "none",
    animation: "pulse2 10s ease-in-out infinite",
  },
  bgCircle3: {
    position: "absolute", top: "40%", right: "-60px",
    width: "200px", height: "200px", borderRadius: "50%",
    background: "rgba(255,255,255,0.03)", pointerEvents: "none",
  },
  /* Grid texture overlay */
  gridOverlay: {
    position: "absolute", inset: 0,
    backgroundImage: "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
    backgroundSize: "40px 40px",
    pointerEvents: "none",
  },

  /* Top bar */
  topBar: {
    position: "relative", zIndex: 2,
    padding: "28px 32px 0",
    display: "flex", alignItems: "center", gap: "12px",
  },
  logoWrap: {
    display: "flex", alignItems: "center", gap: "10px",
  },
  logoIconWrap: {
    width: "40px", height: "40px",
    background: "rgba(212,146,26,0.2)",
    border: "1.5px solid rgba(212,146,26,0.4)",
    borderRadius: "10px",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "20px",
  },
  logoText: {
    display: "flex", flexDirection: "column",
  },
  logoName: {
    color: "#FFFFFF", fontSize: "14px", fontWeight: "800",
    lineHeight: "1.2", letterSpacing: "-0.01em",
  },
  logoSub: {
    color: "rgba(255,255,255,0.5)", fontSize: "10px",
    fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.1em",
  },
  goldPill: {
    marginLeft: "auto",
    background: "rgba(212,146,26,0.18)",
    border: "1px solid rgba(212,146,26,0.4)",
    borderRadius: "50px", padding: "4px 12px",
    fontSize: "10px", fontWeight: "800",
    color: "#D4921A", letterSpacing: "0.08em",
    textTransform: "uppercase",
  },

  /* Main content */
  mainContent: {
    position: "relative", zIndex: 2,
    padding: "24px 32px",
    flex: 1, display: "flex",
    flexDirection: "column", justifyContent: "center",
  },
  slideTitle: {
    fontSize: "11px", fontWeight: "800",
    textTransform: "uppercase", letterSpacing: "0.12em",
    marginBottom: "10px",
    transition: "all 0.5s ease",
  },
  slideHeading: {
    fontSize: "clamp(20px, 2.2vw, 28px)",
    fontWeight: "900", color: "#FFFFFF",
    lineHeight: "1.25", marginBottom: "14px",
    letterSpacing: "-0.02em",
    transition: "all 0.5s ease",
  },
  slideBody: {
    fontSize: "13px", color: "rgba(255,255,255,0.75)",
    lineHeight: "1.7", marginBottom: "22px",
    transition: "all 0.5s ease",
  },

  /* Stats row */
  statsRow: {
    display: "flex", gap: "12px", marginBottom: "24px",
  },
  statBox: {
    flex: 1, background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: "10px", padding: "12px 10px",
    textAlign: "center",
  },
  statVal: {
    fontSize: "18px", fontWeight: "900",
    color: "#FFFFFF", lineHeight: "1",
    marginBottom: "4px",
  },
  statLabel: {
    fontSize: "9px", fontWeight: "700",
    color: "rgba(255,255,255,0.5)",
    textTransform: "uppercase", letterSpacing: "0.08em",
  },

  /* Feature dot nav */
  dotsRow: {
    display: "flex", gap: "6px", marginBottom: "20px",
  },
  dot: {
    height: "4px", borderRadius: "2px",
    background: "rgba(255,255,255,0.25)",
    transition: "all 0.4s ease",
    cursor: "pointer",
  },
  dotActive: {
    background: "#D4921A",
    boxShadow: "0 0 8px rgba(212,146,26,0.6)",
  },

  /* Feature pills strip */
  pillsWrap: {
    display: "flex", flexWrap: "wrap", gap: "6px",
  },
  pill: {
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: "50px", padding: "5px 12px",
    fontSize: "11px", fontWeight: "600",
    color: "rgba(255,255,255,0.8)",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  pillActive: {
    background: "rgba(212,146,26,0.25)",
    border: "1px solid rgba(212,146,26,0.5)",
    color: "#FFD98E",
  },

  /* Bottom strip */
  bottomStrip: {
    position: "relative", zIndex: 2,
    padding: "16px 32px 28px",
    borderTop: "1px solid rgba(255,255,255,0.08)",
  },
  bottomText: {
    fontSize: "11px", color: "rgba(255,255,255,0.4)",
    textAlign: "center", lineHeight: "1.5",
  },
  bottomBrand: {
    color: "#D4921A", fontWeight: "700",
  },

  /* Progress bar */
  progressBar: {
    height: "2px",
    background: "rgba(255,255,255,0.1)",
    borderRadius: "2px", marginBottom: "20px",
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    background: "linear-gradient(90deg, #D4921A, #FFD98E)",
    borderRadius: "2px",
    transition: "width 0.1s linear",
  },
};

/* Inject keyframe animations once */
if (typeof document !== "undefined" && !document.getElementById("mhb-login-panel-styles")) {
  const styleEl = document.createElement("style");
  styleEl.id = "mhb-login-panel-styles";
  styleEl.textContent = `
    @keyframes pulse1 {
      0%, 100% { transform: scale(1) translate(0, 0); opacity: 0.6; }
      50% { transform: scale(1.1) translate(-20px, 20px); opacity: 1; }
    }
    @keyframes pulse2 {
      0%, 100% { transform: scale(1) translate(0, 0); opacity: 0.5; }
      50% { transform: scale(1.15) translate(20px, -15px); opacity: 0.9; }
    }
    @keyframes fadeSlide {
      0% { opacity: 0; transform: translateY(12px); }
      100% { opacity: 1; transform: translateY(0); }
    }
    .mhb-feature-slide {
      animation: fadeSlide 0.5s ease forwards;
    }
    .mhb-pill-hover:hover {
      background: rgba(212,146,26,0.2) !important;
      border-color: rgba(212,146,26,0.4) !important;
      color: #FFD98E !important;
    }
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
  `;
  document.head.appendChild(styleEl);
}

class LoginPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      current: 0,
      progress: 0,
      animKey: 0,
    };
    this.timer = null;
    this.progressTimer = null;
  }

  componentDidMount() {
    this.startCycle();
  }

  componentWillUnmount() {
    this.stopCycle();
  }

  startCycle = () => {
    this.stopCycle();
    this.setState({ progress: 0 });

    // Progress bar tick every 50ms
    const step = (50 / INTERVAL) * 100;
    this.progressTimer = setInterval(() => {
      this.setState((prev) => ({ progress: Math.min(prev.progress + step, 100) }));
    }, 50);

    // Slide advance
    this.timer = setTimeout(() => {
      this.setState(
        (prev) => ({
          current: (prev.current + 1) % FEATURES.length,
          animKey: prev.animKey + 1,
          progress: 0,
        }),
        () => this.startCycle()
      );
    }, INTERVAL);
  };

  stopCycle = () => {
    if (this.timer) clearTimeout(this.timer);
    if (this.progressTimer) clearInterval(this.progressTimer);
  };

  goTo = (idx) => {
    this.stopCycle();
    this.setState({ current: idx, animKey: this.state.animKey + 1, progress: 0 }, () => {
      this.startCycle();
    });
  };

  render() {
    const { current, progress, animKey } = this.state;
    const f = FEATURES[current];

    return (
      <div style={styles.panel}>
        {/* Background effects */}
        <div style={styles.bgCircle1} />
        <div style={styles.bgCircle2} />
        <div style={styles.bgCircle3} />
        <div style={styles.gridOverlay} />

        {/* Top bar */}
        <div style={styles.topBar}>
          <div style={styles.logoWrap}>
            <div style={styles.logoIconWrap}>🏡</div>
            <div style={styles.logoText}>
              <span style={styles.logoName}>My Home Buddha</span>
              <span style={styles.logoSub}>Healthy · Wealthy · Wise</span>
            </div>
          </div>
          <div style={styles.goldPill}>Powered by AI</div>
        </div>

        {/* Main rotating content */}
        <div style={styles.mainContent}>

          {/* Progress bar */}
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${progress}%` }} />
          </div>

          {/* Slide content */}
          <div key={animKey} className="mhb-feature-slide">
            {/* Category label */}
            <div style={{ ...styles.slideTitle, color: f.accent }}>
              {f.icon} &nbsp;{f.category}
            </div>

            {/* Heading */}
            <h2 style={styles.slideHeading}>{f.title}</h2>

            {/* Body */}
            <p style={styles.slideBody}>{f.body}</p>

            {/* Stats */}
            <div style={styles.statsRow}>
              {f.stats.map((s, i) => (
                <div key={i} style={styles.statBox}>
                  <div style={{ ...styles.statVal, color: f.accent }}>{s.val}</div>
                  <div style={styles.statLabel}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Dot navigation */}
          <div style={styles.dotsRow}>
            {FEATURES.map((_, i) => (
              <div
                key={i}
                onClick={() => this.goTo(i)}
                style={{
                  ...styles.dot,
                  ...(i === current ? styles.dotActive : {}),
                  width: i === current ? "28px" : "6px",
                }}
              />
            ))}
          </div>

          {/* Feature pills — click any to jump */}
          <div style={styles.pillsWrap}>
            {FEATURES.map((feat, i) => (
              <div
                key={i}
                className="mhb-pill-hover"
                onClick={() => this.goTo(i)}
                style={{
                  ...styles.pill,
                  ...(i === current ? styles.pillActive : {}),
                }}
              >
                {feat.icon} {feat.category}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom strip */}
        <div style={styles.bottomStrip}>
          <div style={styles.bottomText}>
            Join thousands of buyers, sellers, agents &amp; homeowners on{" "}
            <span style={styles.bottomBrand}>My Home Buddha</span>
            {" "}— the smarter way to buy, sell &amp; manage your home.
          </div>
        </div>
      </div>
    );
  }
}

export default LoginPanel;